type QueryParams = Record<string, string | number | boolean | undefined | null>;

export const AUTH_LOGOUT_EVENT = 'auth:logout';

export class AuthError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'AuthError';
    this.status = status;
  }
}

export const isAuthError = (error: unknown): error is AuthError => {
  return error instanceof AuthError || (
    error instanceof Error &&
    error.name === 'AuthError'
  );
};

export const handleAuthFailure = (status: number, message = '登录已失效，请重新登录') => {
  localStorage.removeItem('auth_token');
  window.dispatchEvent(new CustomEvent(AUTH_LOGOUT_EVENT, {
    detail: { status, message },
  }));
};

const buildUrl = (url: string, params?: QueryParams) => {
  if (!params) return url;

  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });

  const query = searchParams.toString();
  return query ? `${url}${url.includes('?') ? '&' : '?'}${query}` : url;
};

const request = async <T>(url: string, options: RequestInit = {}): Promise<T> => {
  const headers = new Headers(options.headers);
  const token = localStorage.getItem('auth_token');

  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const hasBody = options.body !== undefined && options.body !== null;
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  if (hasBody && !isFormData && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  const contentType = response.headers.get('content-type') || '';
  const payload = contentType.includes('application/json')
    ? await response.json().catch(() => null)
    : await response.text().catch(() => '');

  if (!response.ok) {
    const message =
      (payload && typeof payload === 'object' && ('detail' in payload || 'message' in payload)
        ? String((payload as { detail?: unknown; message?: unknown }).detail || (payload as { message?: unknown }).message)
        : '') || `请求失败 (${response.status})`;
    if (response.status === 401 || response.status === 403) {
      handleAuthFailure(response.status, message);
      throw new AuthError(message, response.status);
    }
    throw new Error(message);
  }

  return payload as T;
};

export const get = <T>(url: string, params?: QueryParams): Promise<T> => {
  return request<T>(buildUrl(url, params));
};

export const post = <T>(url: string, data?: unknown): Promise<T> => {
  return request<T>(url, {
    method: 'POST',
    body: data instanceof FormData ? data : JSON.stringify(data ?? {}),
  });
};

export const put = <T>(url: string, data?: unknown): Promise<T> => {
  return request<T>(url, {
    method: 'PUT',
    body: data instanceof FormData ? data : JSON.stringify(data ?? {}),
  });
};

export const del = <T>(url: string): Promise<T> => {
  return request<T>(url, { method: 'DELETE' });
};
